import React from 'react';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { loadOrCreateUserSettings, saveUserSettings, getOrganizationNameFromCustomers, UserSettings } from '@/shared/services/userSettings';
import { substituteTemplate } from '@/utils/templateSubstitution';
import { useAuth } from '@/shared/contexts/AuthContext';
import { apiClient } from '@/shared/services/api';

interface SettingsContextType {
  scheduledCallMessage: string;
  setScheduledCallMessage: (message: string) => void;
  callSettingsMessage: string;
  setCallSettingsMessage: (message: string) => void;
  requestRecordingMessage: string;
  setRequestRecordingMessage: (message: string) => void;
  // Reminder Message Settings
  reminderMessage: string;
  setReminderMessage: (message: string) => void;
  reminderMinutes: number;
  setReminderMinutes: (minutes: number) => void;
  // Virtual Background Settings
  blurBackground: boolean;
  setBlurBackground: (enabled: boolean) => void;
  // Call Settings
  callAutoRecord: boolean;
  setCallAutoRecord: (enabled: boolean) => void;
  callHideSelfView: boolean;
  setCallHideSelfView: (enabled: boolean) => void;
  callUseBackCamera: boolean;
  setCallUseBackCamera: (enabled: boolean) => void;
  // Company Settings (displayed as "Company Name" in UI)
  organizationName: string;
  setOrganizationName: (name: string) => void;
  systemEmail: string;
  setSystemEmail: (email: string) => void;
  // Timezone Settings
  userTimezone: string;
  setUserTimezone: (timezone: string) => void;
  // Notification Settings
  notifyOnLinkShare: boolean;
  setNotifyOnLinkShare: (enabled: boolean) => void;
  notifyAutoRecording: boolean;
  setNotifyAutoRecording: (enabled: boolean) => void;
  emailNotifications: boolean;
  setEmailNotifications: (enabled: boolean) => void;
  pushNotifications: boolean;
  setPushNotifications: (enabled: boolean) => void;
  smsNotifications: boolean;
  setSmsNotifications: (enabled: boolean) => void;
  productUpdates: boolean;
  setProductUpdates: (enabled: boolean) => void;
  securityAlerts: boolean;
  setSecurityAlerts: (enabled: boolean) => void;
  marketingEmails: boolean;
  setMarketingEmails: (enabled: boolean) => void;
  replaceTokens: (template: string, data: TokenData) => string;
}

interface TokenData {
  name?: string;
  firstName?: string;
  lastName?: string;
  date?: string | Date;
  time?: string | Date;
  scheduledTime?: string;
  organization?: string;
  timeZone?: string;
  userName?: string;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const { user, company } = useAuth();
  const [loading, setLoading] = useState(true);

  // Default messages (fallback if DB is empty)
  const DEFAULT_SCHEDULED_CALL =
    "Hi {FirstName}, you have been invited to have a video call at {time} on {date}. Join the meeting by clicking this link:";
  const DEFAULT_CALL_SETTINGS =
    "Hi {FirstName}, we want to start a live video call with you. Please click this link to join the call:";
  const DEFAULT_REQUEST_RECORDING =
    "Hi {FirstName}, you have been sent a link to record your video. Simply click on the link, record, and hit submit.";
  const DEFAULT_REMINDER = "Hi {FirstName}! Reminder: Your call starts in 30 minutes. Please make sure to be connected to wifi if possible to prevent connection issues.";

  const [scheduledCallMessage, setScheduledCallMessageState] = useState(DEFAULT_SCHEDULED_CALL);
  const [callSettingsMessage, setCallSettingsMessageState] = useState(DEFAULT_CALL_SETTINGS);
  const [requestRecordingMessage, setRequestRecordingMessageState] = useState(DEFAULT_REQUEST_RECORDING);
  const [reminderMessage, setReminderMessageState] = useState(DEFAULT_REMINDER);
  const [reminderMinutes, setReminderMinutesState] = useState(30);
  const [blurBackground, setBlurBackgroundState] = useState(false);
  const [callAutoRecord, setCallAutoRecordState] = useState(true);
  const [callHideSelfView, setCallHideSelfViewState] = useState(false);
  const [callUseBackCamera, setCallUseBackCameraState] = useState(false);
  const [organizationName, setOrganizationNameState] = useState("");
  const [systemEmail, setSystemEmailState] = useState("");
  const [userTimezone, setUserTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [notifyOnLinkShare, setNotifyOnLinkShareState] = useState(true);
  const [notifyAutoRecording, setNotifyAutoRecordingState] = useState(true);
  const [emailNotifications, setEmailNotificationsState] = useState(true);
  const [pushNotifications, setPushNotificationsState] = useState(true);
  const [smsNotifications, setSmsNotificationsState] = useState(false);
  const [productUpdates, setProductUpdatesState] = useState(true);
  const [securityAlerts, setSecurityAlertsState] = useState(true);
  const [marketingEmails, setMarketingEmailsState] = useState(false);

  // Load settings from database on mount
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    loadSettings();
  }, [user, company]); // ✅ FIX: Reload when company data changes

  const loadSettings = async () => {
    try {
      const settings = await loadOrCreateUserSettings();

      // ✅ FIX: Load organization name from AuthContext company data (most reliable source)
      // Fallback chain: company.company_name → user_settings.organization_name → customers table
      let orgName = company?.company_name || settings.organization_name || '';
      if (!orgName && user) {
        const fallbackOrgName = await getOrganizationNameFromCustomers(user.id);
        orgName = fallbackOrgName || '';
      }

      // Set all settings from database
      setScheduledCallMessageState(settings.scheduled_call_message || DEFAULT_SCHEDULED_CALL);
      setCallSettingsMessageState(settings.call_message || DEFAULT_CALL_SETTINGS);
      setRequestRecordingMessageState(settings.request_recording_message || DEFAULT_REQUEST_RECORDING);
      setReminderMessageState(settings.scheduled_reminder_message || DEFAULT_REMINDER);
      setReminderMinutesState((settings.extras?.reminder_minutes as number) || 30);
      setBlurBackgroundState((settings.extras?.blur_background as boolean) || false);
      setCallAutoRecordState(settings.call_auto_record ?? true);
      setCallHideSelfViewState(settings.call_hide_self_view ?? false);
      setCallUseBackCameraState(settings.call_use_back_camera ?? false);
      setOrganizationNameState(orgName);
      setSystemEmailState(settings.system_email || '');
      setNotifyOnLinkShareState(settings.notify_on_link_share ?? true);
      setNotifyAutoRecordingState(settings.notify_auto_recording ?? true);
      setEmailNotificationsState(settings.email_notifications ?? true);
      setPushNotificationsState(settings.push_notifications ?? true);
      setSmsNotificationsState(settings.sms_notifications ?? false);
      setProductUpdatesState(settings.product_updates ?? true);
      setSecurityAlertsState(settings.security_alerts ?? true);
      setMarketingEmailsState(settings.marketing_emails ?? false);

    } catch (error) {
      console.error('[SettingsContext] Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Wrapper functions that save to database
  const setScheduledCallMessage = async (message: string) => {
    setScheduledCallMessageState(message);
    try {
      await saveUserSettings({ scheduled_call_message: message });
    } catch (error) {
      console.error('[SettingsContext] Error saving scheduled call message:', error);
    }
  };

  const setCallSettingsMessage = async (message: string) => {
    setCallSettingsMessageState(message);
    try {
      await saveUserSettings({ call_message: message });
    } catch (error) {
      console.error('[SettingsContext] Error saving call settings message:', error);
    }
  };

  const setRequestRecordingMessage = async (message: string) => {
    setRequestRecordingMessageState(message);
    try {
      await saveUserSettings({ request_recording_message: message });
    } catch (error) {
      console.error('[SettingsContext] Error saving request recording message:', error);
    }
  };

  const setReminderMessage = async (message: string) => {
    setReminderMessageState(message);
    try {
      await saveUserSettings({ scheduled_reminder_message: message });
    } catch (error) {
      console.error('[SettingsContext] Error saving reminder message:', error);
    }
  };

  const setReminderMinutes = async (minutes: number) => {
    setReminderMinutesState(minutes);
    try {
      await saveUserSettings({ extras: { reminder_minutes: minutes } });
    } catch (error) {
      console.error('[SettingsContext] Error saving reminder minutes:', error);
    }
  };

  const setBlurBackground = async (enabled: boolean) => {
    setBlurBackgroundState(enabled);
    // Note: blur_background field doesn't exist in user_settings table yet
    // Store in extras for now
    try {
      await saveUserSettings({ extras: { blur_background: enabled } });
    } catch (error) {
      console.error('[SettingsContext] Error saving blur background:', error);
    }
  };

  const setOrganizationName = async (name: string) => {
    setOrganizationNameState(name);
    try {
      // ✅ FIX: Save to both user_settings AND customers table
      await saveUserSettings({ organization_name: name });

      // Also update the company name in customers table via backend API
      if (company?.id) {
        console.log('[SettingsContext] Updating company name in customers table:', name);
        await apiClient.put(`/user/companies/${company.id}`, { company_name: name });
      }
    } catch (error) {
      console.error('[SettingsContext] Error saving organization name:', error);
    }
  };

  const setSystemEmail = async (email: string) => {
    setSystemEmailState(email);
    try {
      await saveUserSettings({ system_email: email });
    } catch (error) {
      console.error('[SettingsContext] Error saving system email:', error);
    }
  };

  const setCallAutoRecord = async (enabled: boolean) => {
    setCallAutoRecordState(enabled);
    try {
      await saveUserSettings({ call_auto_record: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving call auto record:', error);
    }
  };

  const setCallHideSelfView = async (enabled: boolean) => {
    setCallHideSelfViewState(enabled);
    try {
      await saveUserSettings({ call_hide_self_view: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving call hide self view:', error);
    }
  };

  const setCallUseBackCamera = async (enabled: boolean) => {
    setCallUseBackCameraState(enabled);
    try {
      await saveUserSettings({ call_use_back_camera: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving call use back camera:', error);
    }
  };

  const setNotifyOnLinkShare = async (enabled: boolean) => {
    setNotifyOnLinkShareState(enabled);
    try {
      await saveUserSettings({ notify_on_link_share: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving notify on link share:', error);
    }
  };

  const setNotifyAutoRecording = async (enabled: boolean) => {
    setNotifyAutoRecordingState(enabled);
    try {
      await saveUserSettings({ notify_auto_recording: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving notify auto recording:', error);
    }
  };

  const setEmailNotifications = async (enabled: boolean) => {
    setEmailNotificationsState(enabled);
    try {
      await saveUserSettings({ email_notifications: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving email notifications:', error);
    }
  };

  const setPushNotifications = async (enabled: boolean) => {
    setPushNotificationsState(enabled);
    try {
      await saveUserSettings({ push_notifications: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving push notifications:', error);
    }
  };

  const setSmsNotifications = async (enabled: boolean) => {
    setSmsNotificationsState(enabled);
    try {
      await saveUserSettings({ sms_notifications: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving SMS notifications:', error);
    }
  };

  const setProductUpdates = async (enabled: boolean) => {
    setProductUpdatesState(enabled);
    try {
      await saveUserSettings({ product_updates: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving product updates:', error);
    }
  };

  const setSecurityAlerts = async (enabled: boolean) => {
    setSecurityAlertsState(enabled);
    try {
      await saveUserSettings({ security_alerts: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving security alerts:', error);
    }
  };

  const setMarketingEmails = async (enabled: boolean) => {
    setMarketingEmailsState(enabled);
    try {
      await saveUserSettings({ marketing_emails: enabled });
    } catch (error) {
      console.error('[SettingsContext] Error saving marketing emails:', error);
    }
  };

  const replaceTokens = (template: string, data: TokenData): string => {
    // Use the centralized substituteTemplate utility
    return substituteTemplate(template, {
      name: data.name,
      firstName: data.firstName,
      lastName: data.lastName,
      organization: data.organization || organizationName,
      date: data.date,
      time: data.time,
      timeZone: data.timeZone || userTimezone,
      userName: data.userName,
    });
  };

  return (
    <SettingsContext.Provider
      value={{
        scheduledCallMessage,
        setScheduledCallMessage,
        callSettingsMessage,
        setCallSettingsMessage,
        requestRecordingMessage,
        setRequestRecordingMessage,
        // Reminder Message Settings
        reminderMessage,
        setReminderMessage,
        reminderMinutes,
        setReminderMinutes,
        // Virtual Background Settings
        blurBackground,
        setBlurBackground,
        // Call Settings
        callAutoRecord,
        setCallAutoRecord,
        callHideSelfView,
        setCallHideSelfView,
        callUseBackCamera,
        setCallUseBackCamera,
        // Organization Settings
        organizationName,
        setOrganizationName,
        systemEmail,
        setSystemEmail,
        // Timezone Settings
        userTimezone,
        setUserTimezone,
        // Notification Settings
        notifyOnLinkShare,
        setNotifyOnLinkShare,
        notifyAutoRecording,
        setNotifyAutoRecording,
        emailNotifications,
        setEmailNotifications,
        pushNotifications,
        setPushNotifications,
        smsNotifications,
        setSmsNotifications,
        productUpdates,
        setProductUpdates,
        securityAlerts,
        setSecurityAlerts,
        marketingEmails,
        setMarketingEmails,
        replaceTokens
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
